// Updated at: 1773277028
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { invoiceAPI, clientAPI, productAPI, settingsAPI } from "../lib/api";
import { formatCurrency, getIndianStates } from "../lib/utils";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  ArrowLeft,
  Plus,
  Trash2,
  Loader2,
  Save,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

const emptyItem = {
  product_id: "",
  product_name: "", // Store product name from master (bold in PDF)
  description: "", // Optional description (smaller text below product name)
  hsn_sac_code: "",
  quantity: 1,
  unit: "NOS",
  unit_price: 0,
  discount_percent: 0,
  tax_rate: 18,
};

export default function InvoiceFormPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isEditing = !!id;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [nextNumber, setNextNumber] = useState("");
  const [numberWarning, setNumberWarning] = useState("");
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(false);

  const [formData, setFormData] = useState({
    client_id: searchParams.get("client") || "",
    invoice_number: "", // Allow manual invoice number
    invoice_type: "tax_invoice", // tax_invoice or proforma_invoice
    invoice_date: new Date().toISOString().split("T")[0],
    due_date: "",
    place_of_supply: "",
    tax_type: "igst",
    po_number: "",
    transport: "",
    vehicle_no: "",
    gr_rr_no: "",
    eway_bill: "",
    notes: "",
    terms: "",
    items: [{ ...emptyItem }],
  });

  const [totals, setTotals] = useState({
    subtotal: 0,
    total_discount: 0,
    taxable_amount: 0,
    cgst_amount: 0,
    sgst_amount: 0,
    igst_amount: 0,
    total_tax: 0,
    round_off: 0,
    grand_total: 0,
  });

  useEffect(() => {
    fetchInitialData();
  }, [id]);

  useEffect(() => {
    calculateTotals();
  }, [formData.items, formData.tax_type]);

  // Fetch next number when invoice type changes
  useEffect(() => {
    if (!isEditing) {
      fetchNextNumber();
    }
  }, [formData.invoice_type]);

  const fetchNextNumber = async () => {
    try {
      const nextNumRes = await invoiceAPI.getNextNumber(formData.invoice_type);
      setNextNumber(nextNumRes.data.next_number);
    } catch (e) {
      console.error("Failed to get next number", e);
    }
  };

  const fetchInitialData = async () => {
    try {
      const [clientsRes, productsRes, settingsRes] = await Promise.all([
        clientAPI.list(),
        productAPI.list(),
        settingsAPI.get(),
      ]);

      setClients(clientsRes.data);
      setProducts(productsRes.data);
      setSettings(settingsRes.data);

      if (isEditing) {
        const invoiceRes = await invoiceAPI.get(id);
        const invoice = invoiceRes.data;
        
        // Determine invoice_type from existing data
        let invoiceType = invoice.invoice_type || "tax_invoice";
        if (invoice.is_proforma) {
          invoiceType = "proforma_invoice";
        }
        
        setFormData({
          client_id: invoice.client_id,
          invoice_number: invoice.invoice_number || "",
          invoice_type: invoiceType,
          invoice_date: invoice.invoice_date?.split("T")[0] || "",
          due_date: invoice.due_date?.split("T")[0] || "",
          place_of_supply: invoice.place_of_supply || "",
          tax_type: invoice.tax_type || "igst",
          po_number: invoice.po_number || "",
          transport: invoice.transport || "",
          vehicle_no: invoice.vehicle_no || "",
          gr_rr_no: invoice.gr_rr_no || "",
          eway_bill: invoice.eway_bill || "",
          notes: invoice.notes || "",
          terms: invoice.terms || settingsRes.data.default_terms || "",
          items: invoice.items?.length > 0 ? invoice.items : [{ ...emptyItem }],
        });
      } else {
        // Fetch next invoice number for new invoices
        try {
          const nextNumRes = await invoiceAPI.getNextNumber("tax_invoice");
          setNextNumber(nextNumRes.data.next_number);
        } catch (e) {
          console.error("Failed to get next number", e);
        }
        
        setFormData((prev) => ({
          ...prev,
          terms: settingsRes.data.default_terms || "",
        }));
      }
    } catch (error) {
      toast.error("Failed to load data");
      navigate("/invoices");
    } finally {
      setLoading(false);
    }
  };

  // Check if invoice number exists
  const checkInvoiceNumber = async (number) => {
    if (!number) {
      setNumberWarning("");
      return false;
    }
    try {
      const res = await invoiceAPI.checkNumber(number, formData.invoice_type);
      if (res.data.exists) {
        setNumberWarning(`Invoice number ${number} already exists for this type!`);
        return true;
      } else {
        setNumberWarning("");
        return false;
      }
    } catch (e) {
      console.error("Failed to check number", e);
      return false;
    }
  };

  const calculateTotals = () => {
    let subtotal = 0;
    let totalDiscount = 0;
    let taxableAmount = 0;
    let cgstAmount = 0;
    let sgstAmount = 0;
    let igstAmount = 0;

    formData.items.forEach((item) => {
      const lineTotal = item.quantity * item.unit_price;
      const discount = lineTotal * (item.discount_percent / 100);
      const itemTaxable = lineTotal - discount;
      const itemTax = itemTaxable * (item.tax_rate / 100);

      subtotal += lineTotal;
      totalDiscount += discount;
      taxableAmount += itemTaxable;

      if (formData.tax_type === "igst") {
        igstAmount += itemTax;
      } else if (formData.tax_type === "cgst_sgst") {
        cgstAmount += itemTax / 2;
        sgstAmount += itemTax / 2;
      }
    });

    const totalTax = cgstAmount + sgstAmount + igstAmount;
    let grandTotal = taxableAmount + totalTax;
    let roundOff = 0;

    if (settings?.round_off_enabled !== false) {
      const rounded = Math.round(grandTotal);
      roundOff = rounded - grandTotal;
      grandTotal = rounded;
    }

    setTotals({
      subtotal: Math.round(subtotal * 100) / 100,
      total_discount: Math.round(totalDiscount * 100) / 100,
      taxable_amount: Math.round(taxableAmount * 100) / 100,
      cgst_amount: Math.round(cgstAmount * 100) / 100,
      sgst_amount: Math.round(sgstAmount * 100) / 100,
      igst_amount: Math.round(igstAmount * 100) / 100,
      total_tax: Math.round(totalTax * 100) / 100,
      round_off: Math.round(roundOff * 100) / 100,
      grand_total: Math.round(grandTotal * 100) / 100,
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleInvoiceTypeChange = (newType) => {
    setFormData((prev) => ({ 
      ...prev, 
      invoice_type: newType,
      invoice_number: "" // Reset invoice number when type changes
    }));
    setNumberWarning("");
  };

  const handleClientChange = (clientId) => {
    setFormData((prev) => ({ ...prev, client_id: clientId }));
    
    const client = clients.find((c) => c.id === clientId);
    if (client) {
      // Auto-set due date based on credit days
      if (!formData.due_date && client.credit_days) {
        const dueDate = new Date(formData.invoice_date);
        dueDate.setDate(dueDate.getDate() + client.credit_days);
        setFormData((prev) => ({
          ...prev,
          client_id: clientId,
          due_date: dueDate.toISOString().split("T")[0],
        }));
      }
      
      // Auto-set place of supply from client state
      if (client.state) {
        const state = getIndianStates().find((s) => s.name.toLowerCase() === client.state.toLowerCase());
        if (state) {
          setFormData((prev) => ({
            ...prev,
            place_of_supply: `${state.code}-${state.name}`,
          }));
        }
      }
    }
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...formData.items];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    setFormData((prev) => ({ ...prev, items: updatedItems }));
  };

  const handleProductSelect = (index, productId) => {
    const product = products.find((p) => p.id === productId);
    if (product) {
      const updatedItems = [...formData.items];
      updatedItems[index] = {
        ...updatedItems[index],
        product_id: productId,
        product_name: product.name, // Store product name for PDF (bold)
        description: "", // Clear description, user can add optional description
        hsn_sac_code: product.hsn_sac_code,
        unit: product.unit,
        unit_price: product.default_price,
        tax_rate: product.tax_rate,
      };
      setFormData((prev) => ({ ...prev, items: updatedItems }));
    }
  };

  const addItem = () => {
    setFormData((prev) => ({
      ...prev,
      items: [...prev.items, { ...emptyItem }],
    }));
  };

  const removeItem = (index) => {
    if (formData.items.length === 1) {
      toast.error("At least one item is required");
      return;
    }
    const updatedItems = formData.items.filter((_, i) => i !== index);
    setFormData((prev) => ({ ...prev, items: updatedItems }));
  };

  const handleSubmit = async (e, forceOverride = false) => {
    if (e) e.preventDefault();

    if (!formData.client_id) {
      toast.error("Please select a client");
      return;
    }

    const validItems = formData.items.filter(
      (item) => (item.product_name || item.description) && item.quantity > 0 && item.unit_price > 0
    );

    if (validItems.length === 0) {
      toast.error("Please add at least one valid item");
      return;
    }

    // Check for duplicate invoice number if manual entry
    if (!isEditing && formData.invoice_number && !forceOverride) {
      const isDuplicate = await checkInvoiceNumber(formData.invoice_number);
      if (isDuplicate) {
        setShowDuplicateDialog(true);
        setPendingSubmit(true);
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        invoice_date: new Date(formData.invoice_date).toISOString(),
        due_date: formData.due_date ? new Date(formData.due_date).toISOString() : null,
        is_proforma: formData.invoice_type === "proforma_invoice",
        items: validItems.map((item) => ({
          ...item,
          quantity: parseFloat(item.quantity) || 0,
          unit_price: parseFloat(item.unit_price) || 0,
          discount_percent: parseFloat(item.discount_percent) || 0,
          tax_rate: parseFloat(item.tax_rate) || 0,
        })),
      };

      if (isEditing) {
        await invoiceAPI.update(id, payload);
        toast.success("Invoice updated successfully");
      } else {
        const response = await invoiceAPI.create(payload);
        toast.success("Invoice created successfully");
        navigate(`/invoices/${response.data.id}`);
        return;
      }
      
      navigate(`/invoices/${id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save invoice");
    } finally {
      setSaving(false);
      setPendingSubmit(false);
    }
  };

  const handleDuplicateOverride = () => {
    setShowDuplicateDialog(false);
    handleSubmit(null, true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="invoice-form-page">
      {/* Duplicate Warning Dialog */}
      <AlertDialog open={showDuplicateDialog} onOpenChange={setShowDuplicateDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Duplicate Invoice Number
            </AlertDialogTitle>
            <AlertDialogDescription>
              Invoice number <strong>{formData.invoice_number}</strong> already exists for this invoice type. 
              Do you want to use this number anyway?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingSubmit(false)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDuplicateOverride}>
              Use Anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate("/invoices")}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-900">
            {isEditing ? "Edit Invoice" : "New Invoice"}
          </h1>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Invoice Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Client *</Label>
                  <Select value={formData.client_id} onValueChange={handleClientChange}>
                    <SelectTrigger data-testid="client-select">
                      <SelectValue placeholder="Select client" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map((client) => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Invoice Type</Label>
                  <Select 
                    value={formData.invoice_type} 
                    onValueChange={handleInvoiceTypeChange}
                    disabled={isEditing}
                  >
                    <SelectTrigger data-testid="invoice-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tax_invoice">Tax Invoice</SelectItem>
                      <SelectItem value="proforma_invoice">Proforma Invoice</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {!isEditing && (
                  <div className="space-y-2">
                    <Label>Invoice Number</Label>
                    <Input
                      name="invoice_number"
                      value={formData.invoice_number}
                      onChange={(e) => {
                        handleInputChange(e);
                        checkInvoiceNumber(e.target.value);
                      }}
                      placeholder={nextNumber ? `Auto: ${nextNumber}` : "Auto-generated"}
                      data-testid="invoice-number"
                    />
                    {numberWarning && (
                      <p className="text-xs text-amber-600 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        {numberWarning}
                      </p>
                    )}
                    <p className="text-xs text-slate-500">
                      Leave empty to auto-generate ({formData.invoice_type === "proforma_invoice" ? "Proforma" : "Tax"}: {nextNumber || "loading..."})
                    </p>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Tax Type</Label>
                  <Select
                    value={formData.tax_type}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, tax_type: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="igst">IGST</SelectItem>
                      <SelectItem value="cgst_sgst">CGST + SGST</SelectItem>
                      <SelectItem value="no_tax">No Tax</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Invoice Date</Label>
                  <Input
                    type="date"
                    name="invoice_date"
                    value={formData.invoice_date}
                    onChange={handleInputChange}
                    data-testid="invoice-date"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Due Date</Label>
                  <Input
                    type="date"
                    name="due_date"
                    value={formData.due_date}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Place of Supply</Label>
                  <Select
                    value={formData.place_of_supply}
                    onValueChange={(value) =>
                      setFormData((prev) => ({ ...prev, place_of_supply: value }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {getIndianStates().map((state) => (
                        <SelectItem key={state.code} value={`${state.code}-${state.name}`}>
                          {state.code} - {state.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>PO Number</Label>
                  <Input
                    name="po_number"
                    value={formData.po_number}
                    onChange={handleInputChange}
                    placeholder="Purchase order number"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Transport Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Transport</Label>
                <Input
                  name="transport"
                  value={formData.transport}
                  onChange={handleInputChange}
                  placeholder="Transport company"
                />
              </div>
              <div className="space-y-2">
                <Label>Vehicle No.</Label>
                <Input
                  name="vehicle_no"
                  value={formData.vehicle_no}
                  onChange={handleInputChange}
                  placeholder="Vehicle number"
                />
              </div>
              <div className="space-y-2">
                <Label>GR/RR No.</Label>
                <Input
                  name="gr_rr_no"
                  value={formData.gr_rr_no}
                  onChange={handleInputChange}
                  placeholder="GR/RR number"
                />
              </div>
              <div className="space-y-2">
                <Label>E-Way Bill</Label>
                <Input
                  name="eway_bill"
                  value={formData.eway_bill}
                  onChange={handleInputChange}
                  placeholder="E-way bill number"
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Items */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Invoice Items</CardTitle>
            <Button type="button" variant="outline" size="sm" onClick={addItem}>
              <Plus className="w-4 h-4 mr-2" />
              Add Item
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[60px]">#</TableHead>
                    <TableHead className="min-w-[280px]">Product / Description</TableHead>
                    <TableHead className="w-[120px]">HSN/SAC</TableHead>
                    <TableHead className="w-[80px]">Qty</TableHead>
                    <TableHead className="w-[80px]">Unit</TableHead>
                    <TableHead className="w-[100px]">Rate</TableHead>
                    <TableHead className="w-[80px]">Disc %</TableHead>
                    <TableHead className="w-[80px]">Tax %</TableHead>
                    <TableHead className="w-[100px] text-right">Amount</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {formData.items.map((item, index) => {
                    const lineTotal = item.quantity * item.unit_price;
                    const discount = lineTotal * (item.discount_percent / 100);
                    const taxable = lineTotal - discount;

                    return (
                      <TableRow key={index}>
                        <TableCell className="align-top pt-4">{index + 1}</TableCell>
                        <TableCell className="align-top">
                          <div className="space-y-2">
                            {/* Product Selector */}
                            <Select
                              value={item.product_id || "custom"}
                              onValueChange={(value) =>
                                value === "custom"
                                  ? handleItemChange(index, "product_id", "")
                                  : handleProductSelect(index, value)
                              }
                            >
                              <SelectTrigger className="h-9">
                                <SelectValue placeholder="Select product" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="custom">Custom item</SelectItem>
                                {products.map((p) => (
                                  <SelectItem key={p.id} value={p.id}>
                                    {p.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            
                            {/* Product Name (Bold in PDF) */}
                            <div className="space-y-1">
                              <Input
                                value={item.product_name || ""}
                                onChange={(e) => handleItemChange(index, "product_name", e.target.value)}
                                placeholder="Product Name (shown bold in PDF)"
                                className="h-9 font-semibold"
                                data-testid={`item-product-name-${index}`}
                              />
                              
                              {/* Optional Description (Smaller, Grey in PDF) */}
                              <Input
                                value={item.description || ""}
                                onChange={(e) => handleItemChange(index, "description", e.target.value)}
                                placeholder="Optional description (smaller text below)"
                                className="h-8 text-sm text-slate-600"
                                data-testid={`item-description-${index}`}
                              />
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="align-top pt-4">
                          <Input
                            value={item.hsn_sac_code}
                            onChange={(e) => handleItemChange(index, "hsn_sac_code", e.target.value)}
                            className="h-9 text-sm font-mono"
                          />
                        </TableCell>
                        <TableCell className="align-top pt-4">
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.quantity}
                            onChange={(e) =>
                              handleItemChange(index, "quantity", parseFloat(e.target.value) || 0)
                            }
                            className="h-9 text-sm"
                            data-testid={`item-qty-${index}`}
                          />
                        </TableCell>
                        <TableCell className="align-top pt-4">
                          <Input
                            value={item.unit}
                            onChange={(e) => handleItemChange(index, "unit", e.target.value)}
                            className="h-9 text-sm"
                          />
                        </TableCell>
                        <TableCell className="align-top pt-4">
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.unit_price}
                            onChange={(e) =>
                              handleItemChange(index, "unit_price", parseFloat(e.target.value) || 0)
                            }
                            className="h-9 text-sm font-mono"
                            data-testid={`item-price-${index}`}
                          />
                        </TableCell>
                        <TableCell className="align-top pt-4">
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            value={item.discount_percent}
                            onChange={(e) =>
                              handleItemChange(index, "discount_percent", parseFloat(e.target.value) || 0)
                            }
                            className="h-9 text-sm"
                          />
                        </TableCell>
                        <TableCell className="align-top pt-4">
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.tax_rate}
                            onChange={(e) =>
                              handleItemChange(index, "tax_rate", parseFloat(e.target.value) || 0)
                            }
                            className="h-9 text-sm"
                          />
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm align-top pt-4">
                          {formatCurrency(taxable)}
                        </TableCell>
                        <TableCell className="align-top pt-4">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeItem(index)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Totals & Notes */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Notes & Terms</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder="Additional notes for the client"
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label>Terms & Conditions</Label>
                <Textarea
                  name="terms"
                  value={formData.terms}
                  onChange={handleInputChange}
                  placeholder="Payment terms and conditions"
                  rows={4}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Invoice Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Subtotal</span>
                  <span className="font-mono">{formatCurrency(totals.subtotal)}</span>
                </div>
                {totals.total_discount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Discount</span>
                    <span className="font-mono text-red-600">-{formatCurrency(totals.total_discount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Taxable Amount</span>
                  <span className="font-mono">{formatCurrency(totals.taxable_amount)}</span>
                </div>
                
                {formData.tax_type === "igst" && totals.igst_amount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">IGST (18%)</span>
                    <span className="font-mono">{formatCurrency(totals.igst_amount)}</span>
                  </div>
                )}
                
                {formData.tax_type === "cgst_sgst" && (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">CGST (9%)</span>
                      <span className="font-mono">{formatCurrency(totals.cgst_amount)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">SGST (9%)</span>
                      <span className="font-mono">{formatCurrency(totals.sgst_amount)}</span>
                    </div>
                  </>
                )}
                
                {totals.round_off !== 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Round Off</span>
                    <span className="font-mono">{formatCurrency(totals.round_off)}</span>
                  </div>
                )}
                
                <div className="flex justify-between pt-3 border-t">
                  <span className="font-semibold">Grand Total</span>
                  <span className="font-mono font-bold text-lg">{formatCurrency(totals.grand_total)}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-4">
          <Button type="button" variant="outline" onClick={() => navigate("/invoices")}>
            Cancel
          </Button>
          <Button type="submit" disabled={saving} data-testid="save-invoice-btn">
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {isEditing ? "Update Invoice" : "Create Invoice"}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
